import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Send, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const SYSTEMS = [
  "Nintendo Entertainment System (NES)",
  "Super Nintendo (SNES)",
  "Nintendo 64",
  "GameCube",
  "Wii",
  "Wii U",
  "Switch",
  "Game Boy / Color",
  "Game Boy Advance",
  "Nintendo DS",
  "Nintendo 3DS",
  "Sega Genesis",
  "Sega Dreamcast",
  "Atari Lynx",
  "PlayStation 1",
  "PlayStation 2",
  "PlayStation 3",
  "PlayStation 4",
  "PlayStation 5",
  "PSP / Vita",
  "Xbox",
  "Xbox 360",
  "Xbox One",
  "Xbox Series",
  "PC",
  "Other",
];

const CONDITIONS = [
  { value: "any", label: "Any condition" },
  { value: "loose", label: "Loose / Cart only" },
  { value: "complete", label: "Complete in box (CIB)" },
  { value: "sealed", label: "Sealed / New" },
  { value: "graded", label: "Graded" },
];

const Request = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<string | null>(null);

  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    item_name: "",
    system: "",
    condition: "any",
    notes: "",
  });

  const update = (k: keyof typeof form, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !form.item_name) {
      toast({
        title: "Missing info",
        description: "Name, email, and item are required.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    const { data: userData } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from("customer_requests")
      .insert({
        ...form,
        user_id: userData.user?.id ?? null,
      })
      .select("request_number")
      .single();
    setSubmitting(false);
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    setSubmitted(data.request_number);
    toast({
      title: "Request sent!",
      description: `We'll be in touch about ${form.item_name}.`,
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-6 bg-card border border-border rounded-lg p-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <MessageSquare className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-mono text-foreground">
              REQUEST RECEIVED
            </h1>
            <p className="text-xs font-mono text-muted-foreground mt-2">
              Confirmation: {submitted}
            </p>
          </div>
          <p className="text-sm font-mono text-muted-foreground">
            Thanks! We'll reach out at{" "}
            <span className="text-foreground">{form.customer_email}</span> when
            we track down your item.
          </p>
          <div className="flex gap-2 justify-center">
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              size="sm"
              className="font-mono text-xs"
            >
              <ArrowLeft className="w-3 h-3 mr-1" /> HOME
            </Button>
            <Button
              onClick={() => {
                setSubmitted(null);
                setForm({
                  customer_name: form.customer_name,
                  customer_email: form.customer_email,
                  customer_phone: form.customer_phone,
                  item_name: "",
                  system: "",
                  condition: "any",
                  notes: "",
                });
              }}
              size="sm"
              className="bg-primary text-primary-foreground font-mono text-xs"
            >
              REQUEST ANOTHER
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Button
          onClick={() => navigate("/")}
          variant="outline"
          size="sm"
          className="font-mono text-xs mb-6"
        >
          <ArrowLeft className="w-3 h-3 mr-1" /> BACK
        </Button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold font-mono text-foreground">
            REQUEST AN ITEM
          </h1>
          <p className="text-xs font-mono text-muted-foreground mt-1">
            Can't find what you're looking for? Tell us and we'll track it down.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-6 bg-card border border-border rounded-lg p-6"
        >
          {/* Customer info */}
          <div className="space-y-4">
            <h2 className="text-xs font-mono text-primary uppercase tracking-wider">
              Your Info
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer_name" className="font-mono text-xs">
                  Full Name *
                </Label>
                <Input
                  id="customer_name"
                  value={form.customer_name}
                  onChange={(e) => update("customer_name", e.target.value)}
                  required
                  className="font-mono text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_email" className="font-mono text-xs">
                  Email *
                </Label>
                <Input
                  id="customer_email"
                  type="email"
                  value={form.customer_email}
                  onChange={(e) => update("customer_email", e.target.value)}
                  required
                  className="font-mono text-sm"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="customer_phone" className="font-mono text-xs">
                  Phone (optional)
                </Label>
                <Input
                  id="customer_phone"
                  type="tel"
                  value={form.customer_phone}
                  onChange={(e) => update("customer_phone", e.target.value)}
                  className="font-mono text-sm"
                />
              </div>
            </div>
          </div>

          {/* Item info */}
          <div className="space-y-4">
            <h2 className="text-xs font-mono text-primary uppercase tracking-wider">
              Item You Want
            </h2>
            <div className="space-y-2">
              <Label htmlFor="item_name" className="font-mono text-xs">
                Item Name *
              </Label>
              <Input
                id="item_name"
                placeholder="e.g. The Legend of Zelda: Majora's Mask"
                value={form.item_name}
                onChange={(e) => update("item_name", e.target.value)}
                required
                className="font-mono text-sm"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="system" className="font-mono text-xs">
                  System / Platform
                </Label>
                <Select
                  value={form.system}
                  onValueChange={(v) => update("system", v)}
                >
                  <SelectTrigger
                    id="system"
                    className="font-mono text-sm bg-background"
                  >
                    <SelectValue placeholder="Select a system" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover">
                    {SYSTEMS.map((s) => (
                      <SelectItem key={s} value={s} className="font-mono text-sm">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="condition" className="font-mono text-xs">
                  Desired Condition
                </Label>
                <Select
                  value={form.condition}
                  onValueChange={(v) => update("condition", v)}
                >
                  <SelectTrigger
                    id="condition"
                    className="font-mono text-sm bg-background"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover">
                    {CONDITIONS.map((c) => (
                      <SelectItem
                        key={c.value}
                        value={c.value}
                        className="font-mono text-sm"
                      >
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes" className="font-mono text-xs">
                Additional Notes (optional)
              </Label>
              <Textarea
                id="notes"
                placeholder="Region, max budget, must-have variant, etc."
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                rows={3}
                className="font-mono text-sm"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-primary text-primary-foreground font-mono"
          >
            <Send className="w-4 h-4 mr-2" />
            {submitting ? "SENDING..." : "SUBMIT REQUEST"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Request;
